<script lang="ts">
import type { TIconValue } from '@/utils/helper-objects'
import type { DateTime } from 'luxon'
import { type PropType, defineComponent } from 'vue'

import GameCardProgress from './GameCardProgress.vue'
import GamePrice from './GamePrice.vue'
import PrizeCard from './PrizeCard.vue'

/**
 * A card to display a game.
 */
export default defineComponent({
  components: {
    GamePrice,
    PrizeCard,
    GameCardProgress,
  },
  props: {
    /**
     * Controls whether the progress bar should be presented in the loading
     * state. If this property is `true`, the value of the `currentPogress`
     * property will be ignored.
     */
    progressLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * The cost of a single ticket for the game in GBP (or the localised
     * currency). Can be fractional, i.e `0.99` for 99p.
     */
    ticketCost: {
      type: Number,
      required: true,
    },
    /**
     * The URL of the image to show in the card for this game. The image should
     * be 5:4 aspect ratio. The image will be scaled to fill the available
     * space, ensuring it completely covers the card area.
     */
    image: {
      type: String,
      required: true,
    },
    /**
     * The title of the game this card represents. Used as ALT text for the
     * image.
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * The total value of all the prizes for this game in GBP (or the localised
     * currency). Formatted as an integer.
     */
    totalPrizeValue: {
      type: Number,
      required: true,
    },
    /**
     * The date this game will close.
     */
    closeDate: {
      type: Object as PropType<DateTime>,
      required: true,
    },
    /**
     * Set default countdown threshold as 7
     */
    countdownThreshold: {
      type: Number,
      default: 7,
    },
    /**
     * Set countdown text if there is any
     */
    countdownText: {
      type: String,
      required: false,
    },
    /**
     * The number of prizes left for this game.
     */
    prizesLeft: {
      type: Number,
      required: true,
    },
    /**
     * The icon to display for the game.
     */
    icon: {
      type: String as PropType<TIconValue>,
      required: true,
    },
  },
})
</script>

<template>
  <PrizeCard>
    <template #title>
      <div class="flex bg-black text-white">
        <div class="flex-1 overflow-hidden p-2 text-sm @sm/main:text-base">
          <AppCountdown
            :datetime="closeDate"
            :countdown-threshold="countdownThreshold"
            :countdown-text="countdownText"
            time-class="font-bold"
            class="m-auto w-max whitespace-nowrap"
          />
        </div>
        <GamePrice :price="ticketCost" />
      </div>
    </template>

    <template #image>
      <div class="p-3 @sm/main:p-4 w-full">
        <img
          :src="image"
          :alt="title"
          :class="`w-full object-cover rounded-md ${prizesLeft === 0 && 'grayscale'}`"
        />
      </div>
    </template>

    <GameCardProgress :prizes-left="prizesLeft" :is-loading="progressLoading" />

    <slot></slot>

    <div
      class="line-clamp-1 pt-2 font-bold text-md @sm/main:text-xl flex gap-1 items-center whitespace-nowrap"
    >
      <AppIcon :icon="icon" size="xs" /> {{ title }}
    </div>

    <div class="line-clamp-1 font-medium text-slate-600">
      {{
        $n(totalPrizeValue || 0, 'currency', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      }}
      in Prizes
    </div>
  </PrizeCard>
</template>
